import { Component, OnInit } from '@angular/core';
import { ApAlertService, ApBaseComponent, ApNotificationService } from 'webcommon/core-ui';
import { FpSearchService } from '../services/FpSearchService';
import { finalize, map } from 'rxjs/operators';
import { FacilityPatientSearchResultItem } from './FacilityPatientSearchResultItem';
import { FormControl, FormGroup, } from '@angular/forms';
import moment from 'moment';
import { BrowserService, FindControlIdService, TransitionManagerService, WebCommonStorageRepository } from 'webcommon/legacy-common';
import { FacilityVisitRepository, FpConfigurationRepository } from 'webcommon/webapi';
import { AprimaTransitionRequest, BsDatepickerValue, FpConfigurationDto, FpSearchParametersDto, FpUpdateUsageMetricDto } from 'webcommon/shared';
import { FpUserSettingsService } from '../services/FpUserSettingsService';

@Component({
  providers: [ ApAlertService ],
  styleUrls: ['./fp-search.component.less'],
  selector: 'fp-search',
  templateUrl: './fp-search.component.html',
})
export class FpSearchComponent extends ApBaseComponent implements OnInit {
  constructor(
    public findControlIdService: FindControlIdService,
    private searchService: FpSearchService,
    private facilityVisitRepository: FacilityVisitRepository,
    private transitionManager: TransitionManagerService,
    private alertService: ApAlertService,
    private apNotificationService: ApNotificationService,
    private fpUserSettingsService: FpUserSettingsService,
    private webCommonStorageRepository: WebCommonStorageRepository,
    private fpConfigurationRepository: FpConfigurationRepository,
    private browserService: BrowserService
    ) {
    super();
  }
  loadingTemplates = false;
  searchResults: FacilityPatientSearchResultItem[];
  lastSearch: FpSearchParametersDto;
  fpConfiguration: FpConfigurationDto;
  patient = [];
  downloadingCounter = 0;
  dataLoading = false;
  settingsLoading = false;
  selectedServiceSiteId: string | null;
  selectedPatientProgramIds: string[] = [];
  isActiveDirectoryUser = false;
  showServiceSiteColumn = false;
  showProgramColumn = false;
  showATSAction = false;

  selectDateOfBirth?: Date;
  selectedFromDate?: Date;
  selectedToDate?: Date;

  lastNameControl = new FormControl<string | null>(null);
  firstNameControl = new FormControl<string | null>(null);
  phoneNumberControl = new FormControl<string | null>(null);
  ssnControl = new FormControl<string | null>(null);
  externalIdControl = new FormControl<string | null>(null);
  dateOfBirthControl = new FormControl<BsDatepickerValue>(undefined);
  fromDateControl = new FormControl<BsDatepickerValue>(undefined);
  toDateControl = new FormControl<BsDatepickerValue>(undefined);

  searchFormGroup = new FormGroup({
    firstNameControl: this.firstNameControl,
    lastNameControl: this.lastNameControl,
    phoneNumberControl: this.phoneNumberControl,
    ssnControl: this.ssnControl,
    externalIdControl: this.externalIdControl,
    dateOfBirthControl: this.dateOfBirthControl,
    fromDateControl: this.fromDateControl,
    toDateControl: this.toDateControl,
  });

  updateUsageMetricDto: FpUpdateUsageMetricDto = {
    MetricTypeName: '170.315(e)(1) - View, Download, and Transmit to 3rd Party',
    MetricSubTypeName: 'Success',
    IncrementFactor: 1,
  };

  ngOnInit(): void {
    this.isActiveDirectoryUser = this.webCommonStorageRepository.getIsActiveDirectoryUser();
    this.settingsLoading = true;
    this.searchResults = this.searchService.lastSearchResults;
    this.lastSearch = this.searchService.lastSearch;
    this.setDefaultSearchDates();
    this.loadForm();

    this.fpUserSettingsService.getFpUserProperties().pipe(
      finalize(() => {
        this.settingsLoading = false;
      }),
      this.takeUntilDestroy(),
    ).subscribe(
      (result) => {
        this.showServiceSiteColumn = !!result.ServiceSitesForFacilityPortalUser?.length;
        this.showProgramColumn = !!result.PatientProgramsForFacilityPortalUser?.length;
      },
      (error: unknown) => {
        // eslint-disable-next-line max-len
        this.apNotificationService.error('An error occurred while searching.  Please try again or contact your practice for further assistance.');
        throw error;
      },
    );

    this.fpConfigurationRepository.getFacilityPortalConfiguration().pipe(this.takeUntilDestroy())
    .subscribe(
      (result) => {
      this.fpConfiguration = result;
      if (this.fpConfiguration?.HarmonyCaresIntegrationPartner?.IsLicensed) {
        this.showATSAction = true;
        }
      }
    );
  }

  loadForm() {
    this.lastNameControl.setValue(this.lastSearch?.LastName);
    this.firstNameControl.setValue(this.lastSearch?.FirstName);
    this.phoneNumberControl.setValue(this.lastSearch?.PhoneNumber);
    this.ssnControl.setValue(this.lastSearch?.SocialSecurityNumber);
    this.externalIdControl.setValue(this.lastSearch?.ExternalId);
    this.dateOfBirthControl.setValue(this.lastSearch?.DateOfBirth
      ? new Date(this.lastSearch.DateOfBirth)
      : undefined);
    if (this.lastSearch) {
      if (this.lastSearch.FromDate) {
        this.fromDateControl.setValue(new Date(this.lastSearch.FromDate));
        this.selectedFromDate = new Date(this.lastSearch.FromDate);
      }
      if (this.lastSearch.ToDate) {
        this.toDateControl.setValue(new Date(this.lastSearch.ToDate));
        this.selectedToDate = new Date(this.lastSearch.ToDate);
      }
    }
    this.searchFormGroup.updateValueAndValidity();
    this.selectedServiceSiteId = this.lastSearch?.ServiceSite;
    this.selectedPatientProgramIds = this.lastSearch?.PatientPrograms ?? [];
  }

  setDefaultSearchDates() {
    const toDate = new Date();
    if (this.toDateControl) {
      this.toDateControl.setValue(toDate);
      this.selectedToDate = toDate;
    }

    const fromDate = new Date(toDate);
    if (this.fromDateControl) {
      fromDate.setMonth((fromDate.getMonth() - 6));
      this.fromDateControl.setValue(fromDate);
      this.selectedFromDate = fromDate;
    }
  }

  // called when user clicks clear button
  clear() {
    this.searchService.clearLastSearch();
    this.lastNameControl.setValue(null);
    this.firstNameControl.setValue(null);
    this.phoneNumberControl.setValue(null);
    this.ssnControl.setValue(null);
    this.externalIdControl.setValue(null);
    this.dateOfBirthControl.setValue(undefined);
    this.fromDateControl.setValue(undefined);
    this.selectedFromDate = undefined;
    this.toDateControl.setValue(undefined);
    this.selectedToDate = undefined;
    this.searchResults = [];
    this.selectedServiceSiteId = null;
    this.selectedPatientProgramIds = [];
  }

  onDateOfBirthChanged(date: BsDatepickerValue) {
    this.selectDateOfBirth = date;
  }

  onFromDateChanged(date: BsDatepickerValue) {
    this.selectedFromDate = date;
  }

  onToDateChanged(date: BsDatepickerValue) {
    this.selectedToDate = date;
  }

  // called when user clicks search button/enter, searches based on inputted query
  search() {
    this.alertService.clearAllAlerts();

    if (this.searchFormGroup.invalid) {
      return;
    }
    const rawForm = this.searchFormGroup.getRawValue();
    const userQuery: FpSearchParametersDto = {
      FirstName: rawForm.firstNameControl,
      LastName: rawForm.lastNameControl,
      PhoneNumber: rawForm.phoneNumberControl,
      SocialSecurityNumber: rawForm.ssnControl,
      ExternalId: rawForm.externalIdControl,
      DateOfBirth: rawForm.dateOfBirthControl
        ? rawForm.dateOfBirthControl.toLocaleString()
        : null,
      FromDate: rawForm.fromDateControl
        ? rawForm.fromDateControl.toLocaleString()
        : null,
      ToDate: rawForm.toDateControl
        ? rawForm.toDateControl.toLocaleString()
        : null,
      ServiceSite: this.selectedServiceSiteId,
      PatientPrograms: this.selectedPatientProgramIds,
    };
    // no search term entered, toast notification to tell user to enter search term
    if (!userQuery.FirstName &&
      !userQuery.LastName &&
      !userQuery.PhoneNumber &&
      !userQuery.SocialSecurityNumber &&
      !userQuery.ExternalId &&
      !userQuery.DateOfBirth &&
      !userQuery.FromDate &&
      !userQuery.ToDate &&
      !userQuery.ServiceSite &&
      !userQuery.PatientPrograms?.length
    ) {
        this.alertService.error('Please enter search terms');
        return;
    }
    this.dataLoading = true; // turn loading symbol on

    this.searchService.getPatients(userQuery).pipe(
      finalize(() => {
        this.dataLoading = false;
      }),
      this.takeUntilDestroy(),
    ).subscribe(
      (result) => {
        this.searchResults = result;
      },
      (error: unknown) => {
        // eslint-disable-next-line max-len
        this.apNotificationService.error('An error occurred while searching.  Please try again or contact your practice for further assistance.');
        throw error;
      },
    );
  }

  downloadVisitRef = (result: FacilityPatientSearchResultItem) => {
    return this.downloadVisit(result);
  };

  downloadVisit(result: FacilityPatientSearchResultItem) {
    if (this.downloadingCounter > 4) {
      this.apNotificationService.warning('Maximum downloading visits have been reached. Please try again momentarily.');
      return;
    }

    this.downloadingCounter++;
    result.Downloading = true;
    let metricSubTypeName = 'Success';
    return this.facilityVisitRepository.getPdfByVisit(result.VisitId, result.PatientId).pipe(
      map(
        (data) => {
          result.Downloaded = true;
          return {
            Data: data.Data,
            Extension: data.Extension,
            FileName: result.ExternalId + '_Visit_' + moment(result.VisitDate).format('l')
          };
        },
        (error: unknown) => {
          metricSubTypeName = 'Failure';
          // eslint-disable-next-line max-len
          this.apNotificationService.error('An error occurred while downloading visit.  Please try again or contact your practice for further assistance.');
          throw error;
        },
      ),
      finalize(() => {
        this.updateUsageMetric(metricSubTypeName);
        result.Downloading = false;
        this.downloadingCounter--;
      }),
      this.takeUntilDestroy(),
    );
  }

  updateUsageMetric(metricSubTypeName: string) {
    this.updateUsageMetricDto.MetricSubTypeName = metricSubTypeName;
    this.facilityVisitRepository.postUsageMetricUpdate(this.updateUsageMetricDto)
      .pipe(
        this.takeUntilDestroy(),
      )
      .subscribe();
  }

  display(result: FacilityPatientSearchResultItem) {
    const patientSearchResult = {
        patient: result,
    };
    const request: AprimaTransitionRequest = {
      appRoute: 'display',
      opts: { patientSearchResult },
    };
    this.transitionManager.transition(request);
  }

  openATS(result: FacilityPatientSearchResultItem){
    const userId = this.webCommonStorageRepository.getUserId();
    const url = this.fpConfiguration.HarmonyCaresIntegrationPartner?.Url;

    if (url) {
      this.browserService.getWindow().open(`${url}?patientId=${result.PatientId}&currentUserId=${userId}&origin=facilityPortal`, '_blank');
    } else {
        console.error('URL is undefined');
    }
  }
}
